import {
  requestFunctionJson,
  type FunctionRequestOptions,
} from './functionsTransport';

type ApiEnvelope = {
  success?: boolean;
  error?: string;
  [key: string]: unknown;
};

async function request<T extends ApiEnvelope>(
  path: string,
  options: FunctionRequestOptions = {},
  authenticated = false,
): Promise<T> {
  return requestFunctionJson<T>(`events${path}`, {
    ...options,
    auth: authenticated ? 'current-user' : 'public',
  });
}

export const eventsApi = {
  publicList: (year: number) =>
    request<{
      success: boolean;
      events: Record<string, unknown>[];
      categories: Record<string, unknown>[];
    }>(
      `?year=${encodeURIComponent(year)}`,
    ),

  adminList: (year: number) =>
    request<{
      success: boolean;
      canViewCancelledBookings: boolean;
      events: Record<string, unknown>[];
      bookings: Record<string, unknown>[];
      categories: Record<string, unknown>[];
    }>(`/admin?year=${encodeURIComponent(year)}`, {}, true),

  adminBookingsList: (year: number) =>
    request<{
      success: boolean;
      canViewCancelledBookings: boolean;
      events: Record<string, unknown>[];
      bookings: Record<string, unknown>[];
      categories: Record<string, unknown>[];
    }>(`/admin/bookings?year=${encodeURIComponent(year)}`, {}, true),

  create: (event: Record<string, unknown>) =>
    request<{success: boolean; event: Record<string, unknown>}>(
      '/admin',
      {method: 'POST', body: event},
      true,
    ),

  bulkCreate: async (events: Record<string, unknown>[]) => {
    const CHUNK_SIZE = 50;
    if (events.length <= CHUNK_SIZE) {
      return request<{success: boolean; events: Record<string, unknown>[]}>(
        '/admin/bulk-create',
        {method: 'POST', body: {events}},
        true,
      );
    }
    const created: Record<string, unknown>[] = [];
    for (let i = 0; i < events.length; i += CHUNK_SIZE) {
      const chunk = events.slice(i, i + CHUNK_SIZE);
      const response = await request<{success: boolean; events: Record<string, unknown>[]}>(
        '/admin/bulk-create',
        {method: 'POST', body: {events: chunk}},
        true,
      );
      created.push(...response.events);
    }
    return {success: true, events: created};
  },

  update: (eventId: string, event: Record<string, unknown>) =>
    request<{success: boolean; event: Record<string, unknown>}>(
      `/admin/${encodeURIComponent(eventId)}`,
      {method: 'PUT', body: event},
      true,
    ),

  remove: (eventId: string) =>
    request(`/admin/${encodeURIComponent(eventId)}`, {method: 'DELETE'}, true),

  bulkUpdate: (eventIds: string[], patch: Record<string, unknown>) =>
    request('/admin/bulk-update', {
      method: 'POST',
      body: {eventIds, patch},
    }, true),

  bulkRemove: (eventIds: string[]) =>
    request('/admin/bulk-delete', {
      method: 'POST',
      body: {eventIds},
    }, true),

  updateSeries: (seriesId: string, event: Record<string, unknown>) =>
    request('/admin/series/update', {
      method: 'POST',
      body: {seriesId, event},
    }, true),

  removeSeries: (seriesId: string) =>
    request('/admin/series/delete', {
      method: 'POST',
      body: {seriesId},
    }, true),

  cancelEnrollment: (eventId: string, email?: string) =>
    request(`/admin/${encodeURIComponent(eventId)}/cancel-enrollment`, {
      method: 'POST',
      body: {email},
    }, true),

  rebookEnrollment: (bookingId: string) =>
    request(`/admin/bookings/${encodeURIComponent(bookingId)}/rebook`, {
      method: 'POST',
    }, true),

  // Both target one named devotee — unlike cancelEnrollment, the server
  // rejects a missing email rather than falling back to the oldest booking.
  removeWaitlistEntry: (eventId: string, email: string) =>
    request(`/admin/${encodeURIComponent(eventId)}/remove-waitlist`, {
      method: 'POST',
      body: {email},
    }, true),

  promoteWaitlistEntry: (eventId: string, email: string) =>
    request(`/admin/${encodeURIComponent(eventId)}/promote-waitlist`, {
      method: 'POST',
      body: {email},
    }, true),

  updateBookingDetails: (
    bookingId: string,
    patch: {
      devoteeName?: string;
      email?: string;
      phone?: string;
      address?: string;
      notes?: string;
    },
  ) =>
    request<{success: boolean; booking: Record<string, unknown>}>(
      `/admin/bookings/${encodeURIComponent(bookingId)}`,
      {method: 'PUT', body: patch},
      true,
    ),

  allocate: (eventId: string, allocation: Record<string, unknown>) =>
    request(`/admin/${encodeURIComponent(eventId)}/allocation`, {
      method: 'POST',
      body: allocation,
    }, true),

  joinWaitlist: (
    eventId: string,
    enrollment: Record<string, unknown> & {bookingCode?: string},
    turnstileToken: string,
  ) => {
    const {bookingCode, ...details} = enrollment;
    return request(`/${encodeURIComponent(eventId)}/waitlist`, {
      method: 'POST',
      body: {...details, bookingCode, turnstileToken},
    });
  },

  createCategory: (category: Record<string, unknown>) =>
    request<{success: boolean; category: Record<string, unknown>}>(
      '/admin/categories',
      {method: 'POST', body: category},
      true,
    ),

  updateCategory: (categoryId: string, category: Record<string, unknown>) =>
    request<{success: boolean; category: Record<string, unknown>}>(
      `/admin/categories/${encodeURIComponent(categoryId)}`,
      {method: 'PUT', body: category},
      true,
    ),

  removeCategory: (categoryId: string) =>
    request(`/admin/categories/${encodeURIComponent(categoryId)}`, {method: 'DELETE'}, true),
};
